import { addToast } from '@octano/global-ui';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import {
  reScheduleClass,
  searchClasses,
  updateClassStatus,
} from '../../api/requests/classes';
import useServerSettings from '../../hooks/useServerSettings';
import useTimeModules from '../../hooks/useTimeModules';
import { ClassStatus } from '../../types/Class';
import { ClassRecoveryModal, PastClassesSearch } from './parts';
import { SubmitData as ModalSubmitData } from './parts/ClassRecoveryModal';
import ConfirmRecoveryDialog from './parts/ConfirmRecoveryDialog';
import { toDateOnlyPayload } from '../../utils/dates';
import {
  compareTimes,
  rangesOverlap,
  isRangeRunning,
  makeRange,
} from '../../utils/timeModuleUtils';
import ChangeClassroomModal from '../startClasses/parts/ChangeClassroomModal';

export default function ClassesHistory() {
  const history = useHistory();
  const { t } = useTranslation();

  const [classToRecover, setClassToRecover] = useState<any>();
  const [recoveryFormData, setRecoveryFormData] = useState<ModalSubmitData>();
  const [classToChangeRoom, setClassToChangeRoom] = useState<any>(null);
  const [showChangeRoomModal, setShowChangeRoomModal] = useState(false);
  const { timeModules } = useTimeModules();
  const { offset: offsetUTC } = useServerSettings();
  const searchControls = useRef<any>();

  const isCurrentlyRunning = (
    startTime: string,
    endTime: string,
    date: string,
  ) => {
    return isRangeRunning(makeRange(startTime, endTime), date, offsetUTC);
  };

  const hasConflictWithAnotherClass = async (classData: ModalSubmitData) => {
    const date = toDateOnlyPayload(classData.date);

    const res = await searchClasses({
      sectionId: classToRecover.sectionId,
      page: 0,
      itemsPerPage: Number.MAX_SAFE_INTEGER,
      initialDate: date,
      endDate: date,
    });

    const classesList = res.data?.data?.filter((cd: any) => {
      return (
        cd.status !== ClassStatus.CLOSED && cd.status !== ClassStatus.CANCELLED
      );
    });

    if (!classesList || classesList.length === 0) return false;

    const {
      startTime: newStartTime,
      endTime: newEndTime,
    } = getClassStartAndEndTime(classData);

    if (compareTimes(newEndTime, newStartTime) <= 0) return false;

    return classesList.some((cd: any) => {
      if (cd.id === classToRecover?.id) return false;
      const { sectionSchedule } = cd;
      if (!sectionSchedule) return false;
      const { startModule, endModule } = sectionSchedule;
      if (!startModule || !endModule) return false;
      return rangesOverlap(
        { startTime: newStartTime, endTime: newEndTime },
        { startTime: startModule.startTime, endTime: endModule.endTime },
      );
    });
  };

  const getClassStartAndEndTime = (classData: ModalSubmitData) => {
    const startTime =
      timeModules.find((tm) => tm.id === classData.initModule.id)?.startTime ??
      '00:00';
    const endTime =
      timeModules.find((tm) => tm.id === classData.endModule.id)?.endTime ??
      '00:00';

    return { startTime, endTime };
  };

  const recoverClass = async (formData: ModalSubmitData, force?: boolean) => {
    const hasScheduleConflicts = await hasConflictWithAnotherClass(formData);

    if (!force && hasScheduleConflicts) {
      setRecoveryFormData(formData);
      return;
    }

    setRecoveryFormData(undefined);

    const res = await reScheduleClass({
      dateRecovery: toDateOnlyPayload(formData.date),
      startModuleId: formData.initModule.id,
      endModuleId: formData.endModule.id,
      lessonsId: classToRecover.id,
      sectionId: classToRecover.sectionId,
      classroomId: formData.classroomId,
    });

    const recoveredClassId = res.data?.data.id;

    if (res.error) {
      addToast({
        icon: 'information',
        color: 'danger',
        text: t(`classesHistory.errors.failedRecoveringClass`),
      });
    } else {
      addToast({
        icon: 'check',
        color: 'success',
        text: t(`classesHistory.successMsgs.classScheduled`),
      });

      const { startTime, endTime } = getClassStartAndEndTime(formData);

      const isRunning = isCurrentlyRunning(
        startTime || '',
        endTime || '',
        formData.date,
      );

      if (isRunning) {
        await updateClassStatus({
          status: ClassStatus.INIT,
          id: recoveredClassId as number,
        });
        history.push(`/classes/${recoveredClassId}/attendance`);
      } else {
        setClassToRecover(undefined);

        /* Actualizamos la tabla */
        searchControls.current?.repeatSearch();
      }
    }
  };

  const registerAttendants = async (classData: any) => {
    const { id } = classData;

    const { error } = await updateClassStatus({
      status: ClassStatus.INIT,
      id,
    });

    if (error) {
      addToast({
        icon: 'information',
        color: 'danger',
        text: t(`classesHistory.errors.failedRegisteringClass`),
      });
    } else {
      history.push(`/classes/${id}/attendance`);
    }
  };

  const changeClassroom = async (classroomId: number) => {
    try {
      const [startStr, endStr] = (classToChangeRoom?.schedule || '')
        .split('-')
        .map((s: string) => s.trim());
      const startModule = timeModules.find(
        (tm) => tm.startTime.slice(0, 5) === startStr,
      );
      const endModule = timeModules.find(
        (tm) => tm.endTime.slice(0, 5) === endStr,
      );
      if (!startModule || !endModule) {
        addToast({
          icon: 'information',
          color: 'danger',
          text: t('startClasses.changeClassroomError'),
        });
        return;
      }
      await reScheduleClass({
        dateRecovery: classToChangeRoom.date,
        startModuleId: startModule.id,
        endModuleId: endModule.id,
        lessonsId: classToChangeRoom.id,
        sectionId: classToChangeRoom.sectionId,
        classroomId,
      });
      addToast({
        icon: 'check',
        color: 'success',
        text: t('startClasses.changeClassroomSuccess'),
      });
    } catch (err) {
      addToast({
        icon: 'information',
        color: 'danger',
        text: t('startClasses.changeClassroomError'),
      });
    } finally {
      setShowChangeRoomModal(false);
      setClassToChangeRoom(null);
      searchControls.current?.repeatSearch();
    }
  };

  return (
    <>
      <ChangeClassroomModal
        isOpen={showChangeRoomModal}
        onClose={() => {
          setShowChangeRoomModal(false);
          setClassToChangeRoom(null);
        }}
        onConfirm={changeClassroom}
        lessonId={classToChangeRoom?.id || 0}
        date={classToChangeRoom?.date || dayjs().format('YYYY-MM-DD')}
        schedule={classToChangeRoom?.schedule || ''}
        courseName={classToChangeRoom?.courseName || ''}
        currentClassroomCode={classToChangeRoom?.currentClassroomCode}
        currentClassroomName={classToChangeRoom?.currentClassroomName}
        disabled={!classToChangeRoom}
      />
      <ConfirmRecoveryDialog
        isOpen={!!recoveryFormData}
        onConfirm={() =>
          recoverClass(recoveryFormData as ModalSubmitData, true)
        }
        onCancel={() => setRecoveryFormData(undefined)}
      />

      <ClassRecoveryModal
        isOpen={!!classToRecover}
        onConfirm={recoverClass}
        onCancel={() => setClassToRecover(undefined)}
        courseName={classToRecover?.courseName || ''}
        lessonId={classToRecover?.id}
        currentClassroomCode={classToRecover?.currentClassroomCode}
        currentClassroomName={classToRecover?.currentClassroomName}
      />
      <div className="m-3 py-4 px-3 bg-white rounded">
        <PastClassesSearch
          onClassRegistration={registerAttendants}
          onClassRecovery={(classData: any) => setClassToRecover(classData)}
          onClassChangeClassroom={(classData: any) => {
            setClassToChangeRoom(classData);
            setShowChangeRoomModal(true);
          }}
          ref={searchControls}
        />
      </div>
    </>
  );
}
